import { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import { ArrowLeft, Calendar, MapPin, Users, Camera, Edit, Trash2, Upload, Clock, User, CheckCircle, XCircle, UserPlus, UserMinus } from "lucide-react";
import EventPhotosGrid from "@/components/event-photos-grid";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useEvent } from "../hooks/useEvents";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/config/api";
import { useToast } from "@/hooks/use-toast";

export default function EventDetailPage() {
    const [, params] = useRoute("/event/:id");
    const eventId = params?.id;
    const { data: event, isLoading: eventLoading, error: eventError } = useEvent(eventId!);
    const { user, isAdmin, isPhotographer } = useUser();
    const { toast } = useToast();
    const [photographers, setPhotographers] = useState<any[]>([]);
    const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState<boolean>(false);

    useEffect(() => {
        if (eventId) {
            api.get(`/api/events/${eventId}/photographers`)
                .then(res => {
                    if (res.data.success) {
                        setPhotographers(res.data.data);
                    }
                })
                .catch(err => console.error(err));
        }
    }, [eventId]);

    // Verificar se o usuário está inscrito e se é o proprietário
    useEffect(() => {
        if (event && user?.id && eventId) {
            // Verificar se é o proprietário - o campo no banco é createdbyuserid
            const eventOwnerId = (event as any).createdbyuserid || (event as any).createdByUserId;
            const userIsOwner = eventOwnerId === user.id;
            setIsOwner(userIsOwner);

            // Se não for o proprietário e o usuário for fotógrafo (mesmo que não aprovado), verificar se está inscrito
            // Permitir que fotógrafos pendentes também vejam os botões
            const isUserPhotographer = user?.role === 'photographer';
            if (!userIsOwner && isUserPhotographer) {
                checkSubscription();
            } else {
                setIsSubscribed(false);
                setSubscriptionStatus(null);
            }
        } else {
            setIsSubscribed(false);
            setIsOwner(false);
            setSubscriptionStatus(null);
        }
    }, [event, user, eventId]);

    const checkSubscription = async () => {
        if (!eventId || !user?.id) return;
        
        try {
            setIsLoadingSubscription(true);
            const response = await api.get(`/api/events/${eventId}/subscription`);
            if (response.data.success && response.data.data) {
                setIsSubscribed(true);
                setSubscriptionStatus(response.data.data.status);
            } else {
                setIsSubscribed(false);
                setSubscriptionStatus(null);
            }
        } catch (error) {
            setIsSubscribed(false);
            setSubscriptionStatus(null);
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    const handleSubscribe = async () => {
        if (!eventId) return;
        
        try {
            setIsLoadingSubscription(true);
            const response = await api.post(`/api/events/${eventId}/subscribe`);
            if (response.data.success) {
                toast({
                    title: 'Inscrição realizada!',
                    description: 'Sua solicitação foi enviada para aprovação.',
                });
                await checkSubscription();
                // Atualizar lista de fotógrafos
                const res = await api.get(`/api/events/${eventId}/photographers`);
                if (res.data.success) {
                    setPhotographers(res.data.data);
                }
            }
        } catch (error: any) {
            toast({
                title: 'Erro na inscrição',
                description: error.response?.data?.message || 'Não foi possível realizar a inscrição.',
                variant: 'destructive',
            });
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    const handleUnsubscribe = async () => {
        if (!eventId) return;
        
        if (!confirm('Tem certeza que deseja cancelar sua inscrição neste evento?')) {
            return;
        }

        try {
            setIsLoadingSubscription(true);
            const response = await api.delete(`/api/events/${eventId}/subscribe`);
            if (response.data.success) {
                toast({
                    title: 'Inscrição cancelada',
                    description: 'Sua inscrição foi cancelada com sucesso.',
                });
                setIsSubscribed(false);
                setSubscriptionStatus(null);
                // Atualizar lista de fotógrafos
                const res = await api.get(`/api/events/${eventId}/photographers`);
                if (res.data.success) {
                    setPhotographers(res.data.data);
                }
            }
        } catch (error: any) {
            toast({
                title: 'Erro ao cancelar inscrição',
                description: error.response?.data?.message || 'Não foi possível cancelar a inscrição.',
                variant: 'destructive',
            });
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            'draft': { label: 'Rascunho', variant: 'secondary' as const, color: 'bg-gray-100 text-gray-800' },
            'published': { label: 'Publicado', variant: 'default' as const, color: 'bg-primary/10 text-primary' },
            'archived': { label: 'Arquivado', variant: 'outline' as const, color: 'bg-yellow-100 text-yellow-800' },
            'active': { label: 'Ativo', variant: 'default' as const, color: 'bg-primary/10 text-primary' },
            'upcoming': { label: 'Em Breve', variant: 'secondary' as const, color: 'bg-purple-100 text-purple-800' },
            'completed': { label: 'Finalizado', variant: 'outline' as const, color: 'bg-gray-100 text-gray-800' },
            'cancelled': { label: 'Cancelado', variant: 'destructive' as const, color: 'bg-destructive/10 text-destructive' },
            'Approved': { label: 'Aprovado', variant: 'default' as const, color: 'bg-green-100 text-green-800' },
            'PendingApproval': { label: 'Pendente', variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800' },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
        return (
            <Badge variant={config.variant} className={config.color}>
                {config.label}
            </Badge>
        );
    };

    if (eventLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
                    <div className="h-64 bg-gray-200 rounded mb-6"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2">
                            <div className="h-6 bg-gray-200 rounded mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded mb-2 w-2/3"></div>
                        </div>
                        <div>
                            <div className="h-6 bg-gray-200 rounded mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (eventError || !event) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Evento não encontrado</h1>
                    <p className="text-muted-foreground mb-6">O evento que você está procurando não existe ou foi removido.</p>
                    <Link to="/events">
                        <Button>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Voltar aos Eventos
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>

            {/* Header com imagem de capa */}
            <section className="relative h-64 md:h-80 bg-gradient-to-r from-primary to-teal-600 overflow-hidden">
                {(event as any).coverimage ? (
                    <img
                        src={(event as any).coverimage}
                        alt={`Capa do evento ${event.title}`}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-r from-primary to-teal-600 flex items-center justify-center">
                        <Camera className="h-24 w-24 text-white opacity-30" />
                    </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="container mx-auto">
                        <Link to="/events">
                            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 mb-4">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Voltar aos Eventos
                            </Button>
                        </Link>
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">{event.title}</h1>
                        <div className="flex items-center gap-4">
                            {getStatusBadge(event.status)}
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>{event.date ? new Date(event.date).toLocaleDateString('pt-BR', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                }) : 'Data não informada'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Conteúdo principal */}
            <section className="py-8">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Coluna principal - Descrição */}
                        <div className="lg:col-span-2">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="h-5 w-5" />
                                        Sobre o Evento
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground leading-relaxed">
                                        {event.description || `Reviva os melhores momentos do ${event.title}. Encontre suas fotos!`}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Grid de Fotos */}
                            <Card className="mt-6">
                                <CardContent className="p-6">
                                    <EventPhotosGrid
                                        eventId={eventId!}
                                        eventName={event.title}
                                        eventData={{
                                            monetizationType: event.monetizationType,
                                            pricePerPhoto: event.pricePerPhoto
                                        }}
                                        limit={8}
                                        showTitle={true}
                                        showActions={true}
                                    />


                                </CardContent>
                            </Card>
                        </div>

                        {/* Sidebar - Informações do evento */}
                        <div>
                            <Card>
                                <CardHeader>
                                    <CardTitle>Informações do Evento</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="font-medium">Data e Horário</p>
                                            <p className="text-sm text-muted-foreground">
                                                {event.date ? new Date(event.date).toLocaleDateString('pt-BR', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : 'Data não informada'}
                                            </p>
                                        </div>
                                    </div>

                                    {event.location && (
                                        <div className="flex items-start gap-3">
                                            <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                                            <div>
                                                <p className="font-medium">Local</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {event.location}
                                                </p>
                                            </div>
                                        </div>
                                    )}


                                    <Separator />

                                    <div className="space-y-2">
                                        <Button className="w-full" asChild>
                                            <Link to={`/events/${eventId}/photos`}>
                                                <Camera className="h-4 w-4 mr-2" />
                                                Ver Todas as Fotos
                                            </Link>
                                        </Button>

                                        {/* Botão Inscrever-se / Desinscrever-se - Visível para fotógrafos logados que não são proprietários */}
                                        {user && user.role === 'photographer' && !isOwner && (
                                            <>
                                                {isSubscribed ? (
                                                    <Button 
                                                        variant="outline" 
                                                        className="w-full"
                                                        onClick={handleUnsubscribe}
                                                        disabled={isLoadingSubscription}
                                                    >
                                                        <UserMinus className="h-4 w-4 mr-2" />
                                                        {isLoadingSubscription ? 'Cancelando...' : 'Desinscrever-se'}
                                                    </Button>
                                                ) : (
                                                    <Button 
                                                        className="w-full"
                                                        onClick={handleSubscribe}
                                                        disabled={isLoadingSubscription}
                                                    >
                                                        <UserPlus className="h-4 w-4 mr-2" />
                                                        {isLoadingSubscription ? 'Inscrevendo...' : 'Inscrever-se'}
                                                    </Button>
                                                )}
                                                {subscriptionStatus && (
                                                    <p className="text-xs text-muted-foreground text-center">
                                                        Status: {subscriptionStatus === 'Approved' ? 'Aprovado' : subscriptionStatus === 'Pending' ? 'Pendente' : 'Rejeitado'}
                                                    </p>
                                                )}
                                            </>
                                        )}

                                        {/* Botões de Administração - Visível apenas para Admin */}
                                        {isAdmin && (
                                            <div className="flex gap-2">
                                                <Button variant="outline" size="sm" className="flex-1">
                                                    <Edit className="h-4 w-4 mr-2" />
                                                    Editar
                                                </Button>
                                                <Button variant="destructive" size="sm" className="flex-1">
                                                    <Trash2 className="h-4 w-4 mr-2" />
                                                    Excluir
                                                </Button>
                                            </div>
                                        )}

                                        {/* Botão Upload Foto - Visível apenas para Fotógrafos */}
                                        {isPhotographer && (
                                            <Button variant="outline" className="w-full">
                                                <Upload className="h-4 w-4 mr-2" />
                                                Upload Foto
                                            </Button>
                                        )}

                                    </div>
                                </CardContent>
                            </Card>

                            {/* Informações do Cadastro e Fotógrafos */}
                            <Card className="mt-6">
                                <CardHeader>
                                    <CardTitle>Fotógrafos</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Proprietário */}
                                    <div className="flex items-start gap-3">
                                        <User className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-blue-600">Proprietário</p>
                                            <p className="text-sm font-medium">
                                                {(event as any).ownerName || 'Usuário'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Criador do evento
                                            </p>
                                        </div>
                                    </div>

                                    <Separator />

                                    {/* Inscritos */}
                                    <div>
                                        <p className="font-medium mb-2 flex items-center gap-2">
                                            <Users className="h-4 w-4" />
                                            Inscritos ({photographers.length})
                                        </p>
                                        {photographers.length === 0 ? (
                                            <p className="text-sm text-muted-foreground">Nenhum fotógrafo inscrito.</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {photographers.map((p) => (
                                                    <div key={p.id} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                                                        <div>
                                                            <p className="text-sm font-medium">{p.photographer_name || p.photographer_email}</p>
                                                            <div className="flex items-center gap-1">
                                                                {p.status === 'Approved' && <CheckCircle className="h-3 w-3 text-green-500" />}
                                                                {p.status === 'Pending' && <Clock className="h-3 w-3 text-yellow-500" />}
                                                                {p.status === 'Rejected' && <XCircle className="h-3 w-3 text-red-500" />}
                                                                <span className="text-xs text-muted-foreground">
                                                                    {p.status === 'Approved' ? 'Aprovado' : p.status === 'Pending' ? 'Pendente' : 'Rejeitado'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Informações adicionais */}
                            <Card className="mt-6">
                                <CardHeader>
                                    <CardTitle>Detalhes do Cadastro</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="font-medium">Data de Cadastro</p>
                                            <p className="text-sm text-muted-foreground">
                                                {(event as any).createdat ? new Date((event as any).createdat).toLocaleDateString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : 'Data não informada'}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

